import React from "react";
import { useState, useCallback, useMemo, useEffect } from "react";
import { AdSheetHeader } from "./AdSheetHeader";
import { AdSheetTable } from "./AdSheetTable";
import { CreativeLibraryModal } from "./CreativeLibraryModal";
import { LoadCreativesModal } from "./LoadCreativesModal";
import { ReviewAdsModal } from "./ReviewAdsModal";
import { TemplateSelector } from "@/components/templates/TemplateSelector";
import { FlexibleAssetsDialog } from "@/components/ads/FlexibleAssetsDialog";
import { useAdSheetLogic } from "@/hooks/useAdSheetLogic";
import { type Creative } from "@/hooks/useCreativeLibrary";
import { useBackgroundSync } from "@/hooks/useBackgroundSync";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import type { TemplateFieldSelection } from "@/types/template";
import type { AdRow, FlexibleData } from "@/types/bulkAd";
import type { ResolvedRow } from "@/hooks/useLoadCreatives";
import type { LoadCreativesOutput, SingleRowOutput, CarouselRowOutput, CollectionRowOutput, FlexibleRowOutput } from "./LoadCreativesModal";

const SYNC_STORAGE_KEY = 'last_meta_sync_timestamp';
const SYNC_INTERVAL_MS = 180000; // 3 minutes

interface AdSheetContainerProps {
  sheetId: string;
}

export function AdSheetContainer({ sheetId }: AdSheetContainerProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const { triggerBackgroundSync, getSyncStatus } = useBackgroundSync();

  // Wire up the logic hook
  const {
    rows,
    isLoading,
    isSaving,
    saveError,
    updateRow,
    updateRowBatch,
    addRow,
    addMultipleRows,
    deleteRows,
    duplicateRow,
    dropdownOptions,
    getAdSetsForCampaign,
    getAdSetWithDetails,
    sheetInfo,
    launchAds,
    launchProgress,
    visibleOptionalColumns,
    toggleOptionalColumn,
    selectedRowIds,
    toggleRowSelection,
    selectAllRows,
    clearSelection,
    allRowsSelected,
    someRowsSelected,
    catalogId,
    setCatalogId,
    generateRowName,
  } = useAdSheetLogic(sheetId);

  // Creative library is now used directly in the modal for pairing

  // Local UI state
  const [searchQuery, setSearchQuery] = useState("");
  const [creativeModalOpen, setCreativeModalOpen] = useState(false);
  const [creativeModalSingleSelect, setCreativeModalSingleSelect] = useState(false);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [templateSelectorOpen, setTemplateSelectorOpen] = useState(false);
  const [loadCreativesOpen, setLoadCreativesOpen] = useState(false);
  const [flexibleDialogRowId, setFlexibleDialogRowId] = useState<string | null>(null);

  // Get current sync status
  const syncStatus = user?.id && sheetInfo?.adAccountId 
    ? getSyncStatus(user.id, sheetInfo.adAccountId)
    : { isSyncing: false, lastTriggeredAt: null };

  // Auto-sync on mount if 3 minutes have passed OR dropdowns are empty
  const hasDropdownData = dropdownOptions.campaigns.length > 0 || dropdownOptions.facebookPages.length > 0;
  
  useEffect(() => {
    if (!user?.id || !sheetInfo?.adAccountId) return;

    const lastSyncStr = sessionStorage.getItem(SYNC_STORAGE_KEY);
    const lastSync = lastSyncStr ? parseInt(lastSyncStr, 10) : 0;
    const now = Date.now();

    // Force sync if dropdowns are empty (new sheet or first load) or if interval elapsed
    if (!hasDropdownData || !lastSync || now - lastSync > SYNC_INTERVAL_MS) {
      console.log("Auto-syncing ads data...", !hasDropdownData ? "(dropdowns empty)" : "(interval elapsed)");
      triggerBackgroundSync(user.id, sheetInfo.adAccountId);
      sessionStorage.setItem(SYNC_STORAGE_KEY, now.toString());
    }
  }, [user?.id, sheetInfo?.adAccountId, triggerBackgroundSync, hasDropdownData]);

  // Manual refresh handler
  const handleRefreshData = useCallback(() => {
    if (!user?.id || !sheetInfo?.adAccountId) return;
    
    triggerBackgroundSync(user.id, sheetInfo.adAccountId);
    sessionStorage.setItem(SYNC_STORAGE_KEY, Date.now().toString());
    toast({ title: "Syncing latest data from Meta..." });
  }, [user?.id, sheetInfo?.adAccountId, triggerBackgroundSync, toast]);

  // Filtered rows based on search
  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows;

    const query = searchQuery.toLowerCase();
    return rows.filter(
      (row) =>
        row.adName.toLowerCase().includes(query) ||
        row.headlines[0]?.toLowerCase().includes(query) ||
        row.primaryTexts[0]?.toLowerCase().includes(query)
    );
  }, [rows, searchQuery]);


  // Delete single row (wrapper for deleteRows)
  const deleteRow = useCallback((rowId: string) => {
    deleteRows([rowId]);
  }, [deleteRows]);

  // Duplicate a row
  const handleDuplicateRow = useCallback((rowId: string) => {
    duplicateRow(rowId);
  }, [duplicateRow]);

  // Handle add row
  const handleAddRow = useCallback(() => {
    addRow();
  }, [addRow]);

  const handleSelectCreative = useCallback((rowId: string) => {
    const row = rows.find(r => r.id === rowId);
    const isCollection = row?.adFormat === "Collection";

    // Detect ON_PAGE (Page Likes) — single asset only, use singleSelectMode
    const adSet = row?.campaign?.id && row?.adSet?.id
      ? getAdSetWithDetails(row.campaign.id, row.adSet.id)
      : null;
    const isPageLikes = adSet?.destinationType === "ON_PAGE";

    setEditingRowId(rowId);
    setCreativeModalSingleSelect(isCollection || isPageLikes);
    setCreativeModalOpen(true);
  }, [rows, getAdSetWithDetails]);

  const handleSelectFlexibleCreative = useCallback((rowId: string) => {
    setFlexibleDialogRowId(rowId);
  }, []);

  const handleFlexibleSave = useCallback((data: FlexibleData) => {
    if (!flexibleDialogRowId) return;
    updateRow(flexibleDialogRowId, "flexibleData", data);
    setFlexibleDialogRowId(null);
  }, [flexibleDialogRowId, updateRow]);

  const handleCreativeSelected = useCallback((feed: Creative, vertical: Creative | null, horizontal: Creative | null) => {
    if (!editingRowId) return;

    const feedCreative = {
      id: feed.id,
      filename: feed.filename,
      thumbnail: feed.thumbnailUrl,
      meta_creative_id: feed.metaCreativeId,
      group_type: feed.groupType || undefined,
    };

    let verticalCreative: typeof feedCreative | null = null;
    if (vertical) {
      verticalCreative = {
        id: vertical.id,
        filename: vertical.filename,
        thumbnail: vertical.thumbnailUrl,
        meta_creative_id: vertical.metaCreativeId,
        group_type: vertical.groupType || undefined,
      };
    }

    let horizontalCreative: typeof feedCreative | null = null;
    if (horizontal) {
      horizontalCreative = {
        id: horizontal.id,
        filename: horizontal.filename,
        thumbnail: horizontal.thumbnailUrl,
        meta_creative_id: horizontal.metaCreativeId,
        group_type: horizontal.groupType || undefined,
      };
    }

    const row = rows.find(r => r.id === editingRowId);
    const updates: Partial<AdRow> = {
      creatives: {
        feed: feedCreative,
        vertical: verticalCreative,
        horizontal: horizontalCreative,
      },
      // Store feed creative's tags for naming convention resolution
      creativeTags: (feed as any).tags || undefined,
    };

    // Auto-generate name if locked
    if (row && row.ad_name_locked !== false) {
      const tempRow = { ...row, ...updates };
      const generatedName = generateRowName(tempRow);
      if (generatedName) {
        updates.adName = generatedName;
      }
    }

    updateRowBatch(editingRowId, updates);
    setEditingRowId(null);
  }, [editingRowId, rows, updateRowBatch, generateRowName]);

  // Handle single-creative selection for Collection cover or Page Likes (single asset)
  const handleSingleCreativeSelected = useCallback((creative: Creative) => {
    if (!editingRowId) return;
    const row = rows.find(r => r.id === editingRowId);
    if (!row) return;

    const feedCreative = {
      id: creative.id,
      filename: creative.filename,
      thumbnail: creative.thumbnailUrl,
      meta_creative_id: creative.metaCreativeId,
      group_type: creative.groupType || undefined,
    };

    const isCollection = row.adFormat === "Collection";
    const updates: Record<string, unknown> = {
      creatives: {
        feed: feedCreative,
        vertical: null,
        horizontal: null,
      },
      creativeTags: (creative as any).tags || undefined,
    };

    // Collection-specific: also update collectionData.creativeName
    if (isCollection) {
      updates.collectionData = {
        ...(row.collectionData || {}),
        creativeName: creative.filename,
      } as AdRow["collectionData"];
    }

    // Auto-regenerate ad name
    if (row.ad_name_locked !== false && generateRowName) {
      const tempRow = { ...row, ...updates };
      const generatedName = generateRowName(tempRow);
      if (generatedName) {
        updates.adName = generatedName;
      }
    }

    updateRowBatch(editingRowId, updates);
    setEditingRowId(null);
  }, [editingRowId, rows, updateRowBatch, generateRowName]);

  const handleReviewAds = useCallback(() => {
    setIsReviewOpen(true);
  }, []);

  // Handle rows loaded from Load Creatives modal (Single format - backward compatible)
  const handleLoadCreativeRows = useCallback(async (resolvedRows: ResolvedRow[]) => {
    try {
      await addMultipleRows(resolvedRows);
      toast({ title: "✓ Added rows", description: `Added ${resolvedRows.length} row${resolvedRows.length !== 1 ? "s" : ""} to the ad sheet` });
    } catch {
      toast({ title: "Failed to load creatives", variant: "destructive" });
    }
  }, [addMultipleRows, toast]);

  // Handle format-specific rows from Load Creatives modal
  const handleLoadFormatRows = useCallback(async (outputs: LoadCreativesOutput[]) => {
    try {
      // Build insert-ready rows for addMultipleRows
      const insertRows: Array<{
        ad_format: string;
        ad_name: string;
        creative_feed?: string | null;
        meta_creative_id_feed?: string | null;
        creative_vertical?: string | null;
        meta_creative_id_vertical?: string | null;
        creative_horizontal?: string | null;
        meta_creative_id_horizontal?: string | null;
        thumbnail_url?: string | null;
        carousel_data?: unknown;
        flexible_data?: unknown;
        collection_headline_type?: string | null;
        collection_headline_value?: string | null;
        catalog_cta_type?: string | null;
        description_1?: string | null;
      }> = [];

      for (const output of outputs) {
        if (output.ad_format === "Carousel") {
          const o = output as CarouselRowOutput;
          insertRows.push({
            ad_format: "Carousel",
            ad_name: o.ad_name,
            carousel_data: o.carousel_data,
            thumbnail_url: o.thumbnail_url,
          });
        } else if (output.ad_format === "Collection") {
          const o = output as CollectionRowOutput;
          insertRows.push({
            ad_format: "Collection",
            ad_name: o.ad_name,
            creative_feed: o.collection_creative_name,
            meta_creative_id_feed: o.collection_meta_creative_id,
            thumbnail_url: o.thumbnail_url,
            collection_headline_type: "macro",
            collection_headline_value: o.instant_experience?.product_headline || "{{product.name}}",
            catalog_cta_type: o.instant_experience?.label || "Shop Now",
            description_1: o.instant_experience?.product_description || "{{product.current_price}}",
          });
        } else if (output.ad_format === "Flexible") {
          const o = output as FlexibleRowOutput;
          insertRows.push({
            ad_format: "Flexible",
            ad_name: o.ad_name,
            flexible_data: o.flexible_data,
            thumbnail_url: o.thumbnail_url,
          });
        } else {
          // Single Image / Single Video — handled by onLoadRows callback
          const o = output as SingleRowOutput;
          insertRows.push({
            ad_format: o.ad_format,
            ad_name: o.ad_name,
            creative_feed: o.creative_feed,
            meta_creative_id_feed: o.meta_creative_id_feed,
            creative_vertical: o.creative_vertical,
            meta_creative_id_vertical: o.meta_creative_id_vertical,
            creative_horizontal: o.creative_horizontal,
            meta_creative_id_horizontal: o.meta_creative_id_horizontal,
            thumbnail_url: o.thumbnail_url,
          });
        }
      }

      if (insertRows.length > 0) {
        await addMultipleRows(insertRows);
        toast({
          title: "✓ Added rows",
          description: `Added ${insertRows.length} row${insertRows.length !== 1 ? "s" : ""} to the ad sheet`,
        });
      }
    } catch {
      toast({ title: "Failed to load creatives", variant: "destructive" });
    }
  }, [addMultipleRows, toast]);

  const handleApplyTemplate = useCallback(async (
    templateId: string,
    fieldSelection: TemplateFieldSelection,
    applyTo: 'selected' | 'all'
  ) => {
    console.log('[ApplyTemplate] Starting with templateId:', templateId);
    console.log('[ApplyTemplate] Field selection:', fieldSelection);
    console.log('[ApplyTemplate] Apply to:', applyTo);
    
    // 1. Fetch the full template
    const { data: template, error } = await supabase
      .from('ad_templates')
      .select('*')
      .eq('id', templateId)
      .single();
      
    if (error || !template) {
      console.error('[ApplyTemplate] Failed to fetch template:', error);
      toast({
        title: "Error",
        description: "Failed to fetch template",
        variant: "destructive",
      });
      return;
    }
    
    console.log('[ApplyTemplate] Template fetched:', template);

    // 2. Determine which rows to update
    const rowsToUpdate = applyTo === 'selected' 
      ? rows.filter(row => selectedRowIds.has(row.id))
      : [...rows];

    console.log('[ApplyTemplate] Rows to update:', rowsToUpdate.length);

    if (rowsToUpdate.length === 0) {
      toast({
        title: "No rows to update",
        description: "Please select rows or ensure the sheet has data",
        variant: "destructive",
      });
      return;
    }

    // 3. Apply selected fields to each row
    // IMPORTANT: Use AdRow field names, NOT database column names!
    // For Carousel rows, skip headlines and descriptions (they are per-card)
    for (const row of rowsToUpdate) {
      const updates: Partial<AdRow> = {};
      const isCarousel = row.adFormat === "Carousel";

      if (fieldSelection.primary_texts) {
        if (isCarousel) {
          // Carousel: only apply primary_text_1
          updates.primaryTexts = [
            template.primary_text_1 || '',
            row.primaryTexts?.[1] || '',
            row.primaryTexts?.[2] || '',
            row.primaryTexts?.[3] || '',
            row.primaryTexts?.[4] || '',
          ];
        } else {
          updates.primaryTexts = [
            template.primary_text_1 || '',
            template.primary_text_2 || '',
            template.primary_text_3 || '',
            template.primary_text_4 || '',
            template.primary_text_5 || '',
          ];
        }
      }

      // Skip headlines for Carousel (per-card)
      if (fieldSelection.headlines && !isCarousel) {
        updates.headlines = [
          template.headline_1 || '',
          template.headline_2 || '',
          template.headline_3 || '',
          template.headline_4 || '',
          template.headline_5 || '',
        ];
      }

      if (fieldSelection.cta_button) {
        updates.ctaType = template.cta_button || 'SHOP_NOW';
      }

      if (fieldSelection.url_destination) {
        updates.url = template.url_destination || '';
      }

      if (fieldSelection.url_parameters) {
        updates.utmParameters = template.url_parameters || '';
      }

      if (fieldSelection.facebook_page && template.facebook_page_id) {
        updates.facebookPage = {
          id: template.facebook_page_id,
          name: template.facebook_page_name || '',
        };
      }

      if (fieldSelection.instagram && template.instagram_id) {
        updates.instagramAccount = {
          id: template.instagram_id,
          username: template.instagram_name || '',
        };
      }

      if (fieldSelection.partner_facebook_page && template.partner_facebook_page_id) {
        updates.partnerFacebookPage = {
          id: template.partner_facebook_page_id,
          username: template.partner_facebook_page_name || '',
        };
      }

      if (fieldSelection.partner_instagram && template.partner_instagram_id) {
        updates.partnerInstagramAccount = {
          id: template.partner_instagram_id,
          username: template.partner_instagram_name || '',
        };
      }

      if (fieldSelection.status) {
        updates.status = (template.status as 'PAUSED' | 'ACTIVE') || 'PAUSED';
      }

      // Also store the template ID for enhancement linking
      updates.ad_template_id = templateId;

      console.log('[ApplyTemplate] Updating row:', row.id, 'with updates:', updates);
      
      // Update the row with batch updates
      updateRowBatch(row.id, updates);
      
      console.log('[ApplyTemplate] updateRowBatch called for row:', row.id);
    }

    toast({
      title: "Template Applied",
      description: `Updated ${rowsToUpdate.length} row${rowsToUpdate.length !== 1 ? 's' : ''}`,
    });
  }, [rows, selectedRowIds, updateRowBatch, toast]);

  const handleLaunch = useCallback(async () => {
    const rowIds = rows
      .filter(r => r.stage === 'Draft' && r.adName && r.campaign && r.adSet)
      .map(r => r.id);

    if (rowIds.length === 0) {
      toast({ title: "No ads to launch", variant: "destructive" });
      return { success: false, launched: 0, failed: 0, errors: [] };
    }

    setIsLaunching(true);

    // Call the real function
    const result = await launchAds(rowIds);
    
    if (result.success) {
      toast({ title: "Success", description: `${result.launched} ads launched!` });
    } else {
      toast({ title: "Launch Failed", description: `${result.failed} ads failed`, variant: "destructive" });
    }
    
    setIsLaunching(false);
    setIsReviewOpen(false);
    return result;
  }, [rows, launchAds, toast]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-muted-foreground">Loading ad sheet...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background">
      <AdSheetHeader
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onAddRow={handleAddRow}
        onReviewAds={handleReviewAds}
        rowCount={rows.length}
        isSaving={isSaving}
        saveError={saveError}
        sheetName={sheetInfo?.name}
        isSyncing={syncStatus.isSyncing}
        onRefreshData={handleRefreshData}
        visibleOptionalColumns={visibleOptionalColumns}
        onToggleColumn={toggleOptionalColumn}
        onApplyTemplate={() => setTemplateSelectorOpen(true)}
        selectedCount={selectedRowIds.size}
        onClearSelection={clearSelection}
        catalogId={catalogId}
        onCatalogChange={setCatalogId}
        adAccountId={sheetInfo?.adAccountId}
        userId={user?.id}
        onLoadCreatives={() => setLoadCreativesOpen(true)}
      />
      <div className="flex-1 min-h-0 overflow-auto">
        <AdSheetTable
          rows={filteredRows}
          updateRow={updateRow}
          updateRowBatch={updateRowBatch}
          deleteRow={deleteRow}
          onDuplicateRow={handleDuplicateRow}
          dropdownOptions={dropdownOptions}
          getAdSetsForCampaign={getAdSetsForCampaign}
          getAdSetWithDetails={getAdSetWithDetails}
          onSelectCreative={handleSelectCreative}
          onSelectFlexibleCreative={handleSelectFlexibleCreative}
          visibleOptionalColumns={visibleOptionalColumns}
          adAccountId={sheetInfo?.adAccountId}
          userId={user?.id}
          selectedRowIds={selectedRowIds}
          toggleRowSelection={toggleRowSelection}
          selectAllRows={selectAllRows}
          clearSelection={clearSelection}
          allRowsSelected={allRowsSelected}
          someRowsSelected={someRowsSelected}
          sheetCatalogId={catalogId}
          generateRowName={generateRowName}
        />
      </div>

      {/* Creative Library Modal */}
      <CreativeLibraryModal
        open={creativeModalOpen}
        onOpenChange={setCreativeModalOpen}
        onSelect={handleCreativeSelected}
        onSelectSingle={handleSingleCreativeSelected}
        adAccountId={sheetInfo?.adAccountId}
        adFormat={editingRowId ? rows.find(r => r.id === editingRowId)?.adFormat : undefined}
        singleSelectMode={creativeModalSingleSelect}
      />

      {/* Review & Launch Modal */}
      <ReviewAdsModal
        open={isReviewOpen}
        onOpenChange={setIsReviewOpen}
        rows={rows}
        onLaunch={handleLaunch}
        isLaunching={isLaunching}
        launchProgress={launchProgress}
      />

      {/* Template Selector Modal */}
      <TemplateSelector
        open={templateSelectorOpen}
        onOpenChange={setTemplateSelectorOpen}
        selectedRowCount={selectedRowIds.size}
        totalRowCount={rows.length}
        onApply={handleApplyTemplate}
        adAccountId={sheetInfo?.adAccountId}
      />

      {/* Load Creatives Modal */}
      <LoadCreativesModal
        open={loadCreativesOpen}
        onOpenChange={setLoadCreativesOpen}
        adAccountId={sheetInfo?.adAccountId}
        userId={user?.id}
        onLoadRows={handleLoadCreativeRows}
        onLoadFormatRows={handleLoadFormatRows}
      />

      {/* Flexible Assets Dialog */}
      {flexibleDialogRowId && sheetInfo?.adAccountId && user?.id && (
        <FlexibleAssetsDialog
          isOpen={!!flexibleDialogRowId}
          onClose={() => setFlexibleDialogRowId(null)}
          adAccountId={sheetInfo.adAccountId}
          userId={user.id}
          existingData={rows.find(r => r.id === flexibleDialogRowId)?.flexibleData || null}
          onSave={handleFlexibleSave}
        />
      )}
    </div>
  );
}
